import type {
  GetProcessDraftResponse,
  GetProcessLayoutResponse,
  ProcessDraftStep
} from '@bliks-io/contracts';
import { rewriteSourceStepId } from './draftGraphOperations';

/**
 * Replace an optimistic placeholder (keyed by `tempId`) with the server-returned
 * `realStep`, preserving optimistic state staged onto the placeholder (inputs,
 * actionType, task) and rewriting every `sourceSteps` reference tagged with
 * `tempId` to the real `stableStepId`.
 */
export function reconcilePlaceholderInDraft(
  prev: GetProcessDraftResponse,
  tempId: string,
  realStep: ProcessDraftStep
): GetProcessDraftResponse {
  const realStableId = String(realStep.stableStepId);
  if (tempId === realStableId) return prev;
  const placeholder = prev.steps.find((s) => s._id === tempId);
  if (!placeholder) return prev;
  const mergedRealStep: ProcessDraftStep = {
    ...realStep,
    inputs: placeholder.inputs ?? [],
    actionType: placeholder.actionType ?? realStep.actionType,
    ...(placeholder.task !== undefined ? { task: placeholder.task } : {})
  } as ProcessDraftStep;
  const steps: ProcessDraftStep[] = [];
  for (const s of prev.steps) {
    if (s._id === tempId) continue;
    steps.push(rewriteSourceStepId(s, tempId, realStableId));
  }
  steps.push(mergedRealStep);
  const draftAssignments = prev.draftAssignments.map((a) =>
    rewriteSourceStepId(a, tempId, realStableId)
  );
  return {
    ...prev,
    draft: { ...prev.draft, updatedAt: new Date() },
    steps,
    draftAssignments
  };
}

/**
 * Swap the placeholder layout entry for one keyed on the real stableStepId so
 * the node doesn't flash through the canvas-origin sentinel at `{0,0}` between
 * the draft reconcile and the layout refetch.
 */
export function reconcilePlaceholderInLayout(
  prev: GetProcessLayoutResponse,
  tempId: string,
  realStableId: string
): GetProcessLayoutResponse {
  if (tempId === realStableId) return prev;
  const idx = prev.stepPositions.findIndex((p) => String(p.stableStepId) === tempId);
  if (idx === -1) return prev;
  const swapped = [...prev.stepPositions];
  swapped[idx] = { ...swapped[idx], stableStepId: realStableId };
  return { ...prev, stepPositions: swapped };
}
